import type { Context, Plugin } from '@segment/analytics-next'
import { getOneTrustConsent, transformOneTrustConsent } from '@susu/cookie'
import { getLocaleFromPathname } from '@susu/locale-utils'
import { getSegmentCommonProperties } from '@susu/segment-common'

import { isBrowser } from './env'
import { createGAdata } from './utils'

/**
 * Derives the Segment page `path` from a locale-prefixed pathname.
 * Homepage (`/en-us/`) returns the locale (`en-us`).
 * Other pages (`/en-us/cart`) return the path after the locale (`cart`).
 */
function getSegmentPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length <= 1) {
    return segments[0] ?? ''
  }
  return segments.slice(1).join('/')
}

/**
 * Creates a Segment enrichment plugin that adds consent, locale, page context,
 * and common properties (including marketing click IDs from @susu/segment-common).
 * SSR-safe.
 * @returns Segment Plugin instance configured for Suitsupply GA integration
 */
export const integrationPlugin = ({
  omitProperties = [],
}: { omitProperties?: readonly string[] } = {}): Plugin => {
  let currentUrl: string | undefined
  let referrerUrl: string | undefined

  const withConsentContext = (ctx: Context): Context => {
    if (!isBrowser()) {
      return ctx
    }

    const url = new URL(window.location.href)
    if (url.href !== currentUrl) {
      referrerUrl = currentUrl ?? document.referrer
      currentUrl = url.href
    }
    const referrer = referrerUrl ?? document.referrer

    ctx.updateEvent('context', {
      ...ctx.event.context,
      consent: transformOneTrustConsent(getOneTrustConsent()),
      locale: getLocaleFromPathname(url.pathname),
      page: {
        ...ctx.event.context?.page,
        path: getSegmentPath(url.pathname),
        referrer,
        search: url.search,
        url: url.href,
      },
    })

    const common = getSegmentCommonProperties()
    const properties: Record<string, unknown> = {
      ...ctx.event.properties,
      ...common,
      // A page_type the app set (e.g. storefront's store_overview/store_details,
      // which the path alone can't tell apart) wins over the path-based default.
      page_type: ctx.event.properties?.page_type || common.page_type,
    }
    // Runs after the app's own Avo call, so it also strips keys Avo added.
    for (const key of omitProperties) {
      delete properties[key]
    }
    ctx.updateEvent('properties', properties)

    return ctx
  }

  return {
    name: 'Suitsupply GA Integration Plugin',
    type: 'enrichment',
    version: '1',
    isLoaded: () => true,
    identify: withConsentContext,
    load: createGAdata,
    page: withConsentContext,
    track: withConsentContext,
  }
}
